'use client';

import { c as _c } from "react/compiler-runtime";
import { Liveline, type LivelineSeries } from '@morfi/chart';
import { useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';
import { buildPerSeriesTooltip, liveChartAxisOptions, liveChartLegendStyle, resolveCssVar, zeroBaselineReferenceLine } from '@/components/chart/liveline-crosshair-style';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { buildLiveCrosshairTimeRange } from '@/lib/chart/crosshair-time-range';
import { formatTimeAxisLabel } from '@/lib/chart/time-axis-label';
import { cn } from '@/lib/ui/utils';
import { useChartSettingsStore } from '@/stores/chart-settings';

/** One point of a PnL line. Numbers stop at the canvas boundary; visible text uses `valueD6`. */
export interface PnlChartPoint {
  readonly time: number;
  readonly value: number;
  readonly valueD6: string;
}

/** A single PnL line (Total / Realized / Unrealized). */
export interface PnlChartSeries {
  readonly id: string;
  readonly label: string;
  /** A CSS colour token; resolved before it reaches the canvas boundary. */
  readonly color: string;
  /** Render dashed (used for the Unrealized line). */
  readonly dashed?: boolean;
  readonly points: ReadonlyArray<PnlChartPoint>;
  readonly value: number;
  /** Pre-formatted latest value shown in the built-in legend chip. */
  readonly valueLabel: string;
}
export interface PnlChartCardLivelineProps {
  /** Overlapping (non-stacked) lines. */
  readonly series: ReadonlyArray<PnlChartSeries>;
  /** Show the breathing loading line (first load, before any data). */
  readonly loading: boolean;
  readonly formatAxisValue: (value: number, fractionDigits?: number, tickStep?: number) => string;
  readonly formatTooltipValue: (value: number) => string;
  readonly locale: string;
  readonly windowSecs: number;
  readonly className?: string;
}
const MIN_POINTS = 2;

// Endpoint dot + pulse sized down to match the portfolio value chart
// (winners-row preset) — the full-size default endpoint reads as oversized on
// a thin PnL line.
// Match the market-detail chart canvas height (CHART_CANVAS_HEIGHT_DESKTOP 420 /
// CHART_CANVAS_HEIGHT_MOBILE 280) so the PnL modal chart shares the market
// chart's aspect ratio.
const WELL_HEIGHT_CLASS = 'h-[420px] max-sm:h-[280px]'; // ARBITRARY: market-detail canvas height (420/280)

/**
 * Portfolio PnL chart — canvas-based `<LiveChartSeries>` (overlapping multi-series,
 * NOT stacked) that replaces the static SVG PnL chart. Total / Realized /
 * Unrealized render as distinct animated lines over a zero baseline, with the
 * built-in series-toggle legend, the market-detail per-series-pill hover
 * crosshair, and the chart's built-in loading/empty treatment (the host keeps a
 * keep-previous snapshot so a range switch never flashes empty).
 *
 * Off-screen rendering is gated by an `IntersectionObserver` (200 px prefetch)
 * with a sticky mount latch.
 */
export function PnlChartCardLiveline(t0) {
  const $ = _c(27);
  const {
    series,
    loading,
    formatAxisValue,
    formatTooltipValue,
    locale,
    windowSecs,
    className
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const theme = resolvedTheme === "light" ? "light" : "dark";
  const reduceMotion = useReducedMotion() === true;
  const alwaysShowTooltip = useChartSettingsStore(_temp);
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "200px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(containerRef, t1);
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[1] !== isIntersecting || $[2] !== mounted) {
    t2 = () => {
      if (isIntersecting && !mounted) {
        setMounted(true);
      }
    };
    t3 = [isIntersecting, mounted];
    $[1] = isIntersecting;
    $[2] = mounted;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== series) {
    t4 = series.some(_temp2);
    $[5] = series;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const enoughPoints = t4;
  let t5;
  if ($[7] !== series) {
    t5 = series.map(_temp4);
    $[7] = series;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const chartSeries = t5;
  let t6;
  if ($[9] !== reduceMotion) {
    t6 = reduceMotion ? {
      lerpSpeed: 1
    } : {};
    $[9] = reduceMotion;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const smoothingProp = t6;
  const showChart = mounted && (enoughPoints || loading);
  let t7;
  if ($[11] !== className) {
    t7 = cn("relative flex w-full flex-col", WELL_HEIGHT_CLASS, className);
    $[11] = className;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== alwaysShowTooltip || $[14] !== chartSeries || $[15] !== formatAxisValue || $[16] !== formatTooltipValue || $[17] !== loading || $[18] !== locale || $[19] !== showChart || $[20] !== smoothingProp || $[21] !== theme || $[22] !== windowSecs) {
    t8 = showChart ? <Liveline data={[]} value={0} series={chartSeries} theme={theme} window={windowSecs} loading={loading} referenceLine={zeroBaselineReferenceLine()} grid={true} scrub={true} alwaysShowTooltip={alwaysShowTooltip} pulse={true} dotSize={4.5} pulseSize={14} badge={false} fill={false} momentum={false} edgeFade={false} formatValue={formatAxisValue} formatTime={(time, t9) => {
      const renderedWindowSecs = t9 === undefined ? windowSecs : t9;
      return formatTimeAxisLabel(time, renderedWindowSecs, locale);
    }} crosshairStyle={buildPerSeriesTooltip({
      formatSeriesValue: formatTooltipValue,
      formatTimeRange: (from, to) => buildLiveCrosshairTimeRange(from, to, locale),
      alwaysShow: alwaysShowTooltip
    })} seriesToggleStyle={liveChartLegendStyle} axisOptions={liveChartAxisOptions} {...smoothingProp} className="block w-full min-h-0 flex-1" /> : null;
    $[13] = alwaysShowTooltip;
    $[14] = chartSeries;
    $[15] = formatAxisValue;
    $[16] = formatTooltipValue;
    $[17] = loading;
    $[18] = locale;
    $[19] = showChart;
    $[20] = smoothingProp;
    $[21] = theme;
    $[22] = windowSecs;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <div ref={containerRef} className={t7} data-testid="pnl-chart-card-liveline">{t8}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function _temp4(s) {
  return {
    id: s.id,
    label: s.label,
    color: resolveCssVar(s.color),
    value: s.value,
    data: s.points.map(_temp3)
  };
}
function _temp3(p) {
  return {
    time: p.time,
    value: p.value
  };
}
function _temp2(entry) {
  return entry.points.length >= MIN_POINTS;
}
function _temp(state) {
  return state.alwaysShowTooltip;
}